import { useEffect, useState } from 'react'
import { ArrowIcon } from '@/components/icons'
import * as programsApi from '@/api/programs'
import { trackEvent } from '@/lib/analytics'
import type { ProgramInfo } from '@/types/programs'

/**
 * A one-line invitation on the login page while a program takes sign-ups. The
 * details live behind the login; the program endpoint is public, so name and
 * reward come from the API, and without an open program the strip is gone.
 */
export function ProgramLoginPromo({ slug, onJoin }: { slug: string; onJoin: () => void }) {
  const [program, setProgram] = useState<ProgramInfo | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    programsApi
      .getProgram(slug, controller.signal)
      .then(setProgram)
      .catch(() => undefined)
    return () => controller.abort()
  }, [slug])

  const open = program?.signup.state === 'open'

  useEffect(() => {
    if (open) trackEvent('program_banner_view', { program: slug, state: 'login' })
  }, [open, slug])

  if (!program || !open) return null

  return (
    <section className="mt-10 flex flex-col gap-4 rounded-3xl bg-dark-blue p-6 text-white sm:flex-row sm:items-center md:p-8">
      <span className="inline-flex self-start rounded-full bg-orange px-3 py-1 text-13 font-bold text-white sm:self-center">
        Nieuw
      </span>
      <p className="flex-1 text-base leading-normal text-white/80 md:text-lg">
        <strong className="text-white">{program.name}</strong>: verdien tot €{program.signup.nextRewardEur} door je
        elektrische auto buiten de avondpiek te laden.
      </p>
      <button
        type="button"
        className="btn-cta shrink-0 self-start whitespace-nowrap !rounded-full !px-6 sm:self-center"
        onClick={() => {
          trackEvent('program_banner_click', { program: slug, state: 'login' })
          onJoin()
        }}
      >
        Log in voor details
        <ArrowIcon />
      </button>
    </section>
  )
}
